import {chain} from 'lodash';
import {useMemo} from 'react';

import {isDefined} from '../../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {InboundOptimisticMessage} from '../../../../../../../libs/shared/core/src/models/api/chatDirectPushMessageModel';
import {ResourceMap} from '../../../../../../../libs/shared/core/src/store/commonAppTypes';
import {createSelector} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {getMessages} from '../../../../../../store/data/dataSelectors';

/*
 * Hooks.
 */

/**
 * This hook receives optimistic chat messages and it filters out the ones that have already been processed.
 * The chatDirectPushMessages are temporal.
 * They are needed until they are processed by the backend.
 * They are stored in the component state. If component unmounts, they are removed from the state.
 * @param chatDirectPushMessages the inbound optimistic messages from the chat
 * @param conversationId the conversation linked to the messages
 * @return the optimistic messages to display.
 */
export const useChatOptimisticMessages = (
  chatDirectPushMessages: ResourceMap<ReadonlyArray<InboundOptimisticMessage>>,
  conversationId: number,
) => {
  const optimisticMessages = chatDirectPushMessages[conversationId];

  const optimisticExtIds = useMemo(
    () => new Set((optimisticMessages ?? []).map((message) => message.id)),
    [optimisticMessages],
  );

  // Find all the direct message ext IDs that already exist in the store.
  const getReceivedExtIds = useMemo(
    () =>
      createSelector([getMessages], (messages) =>
        chain(messages)
          .filter(isDefined)
          .filter((message) => message.conversationId === conversationId)
          .map((message) => message.extId)
          .filter((extId) => optimisticExtIds.has(extId ?? ''))
          .value(),
      ),
    [conversationId, optimisticExtIds],
  );
  const receivedExtIds = useAppSelector(getReceivedExtIds);

  const pendingOptimisticMessages =
    optimisticMessages?.filter((message) => !receivedExtIds.includes(message.id)) ?? [];

  return {optimisticMessages: pendingOptimisticMessages};
};
